import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowRight, Mail, MapPin, Phone, CreditCard, Sparkles, Check
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { enrichService } from "../lib/serviceEnrich";
import { Separator } from "../components/ui/separator";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Calendar as CalendarComponent } from "../components/ui/calendar";

import { SquarePaymentForm } from "../components/SquarePaymentForm";
import { ConsultationPolicyModal } from "../components/ConsultationPolicyModal";
import PromoCodeInput from "../components/PromoCodeInput";

import { useAuth } from "../contexts/AppContexts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper local (dupliqué d'App.js pour éviter une dépendance circulaire). Formate une
// Date JS en YYYY-MM-DD en heure LOCALE (sinon décalage UTC = jour précédent en Europe/Paris).
function formatLocalDate(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Libellé du mode "présentiel" : certaines prestations (purification des lieux) se font
// AU DOMICILE du client, pas en boutique. On adapte donc l'étiquette.
function isAtHomeService(svc) {
  return svc?.presentiel_label === 'À domicile' || /lieux/i.test(svc?.name || '');
}
function presentielLabel(svc, { long = false } = {}) {
  if (isAtHomeService(svc)) return 'À domicile';
  return long ? 'En boutique' : 'Boutique';
}

export default function BookingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(location.state?.service || null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [locationMode, setLocationMode] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('pay_at_shop');
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDays, setAvailableDays] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [rgpdConsent, setRgpdConsent] = useState(false);
  // Téléphone obligatoire pour la prise de RDV. Pré-rempli depuis le profil, modifiable.
  const [phoneInput, setPhoneInput] = useState('');
  useEffect(() => { if (user?.phone) setPhoneInput(user.phone); }, [user]);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [suggestedSlots, setSuggestedSlots] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [pendingSuggestedTime, setPendingSuggestedTime] = useState(null);
  const [bookingPromo, setBookingPromo] = useState(null); // code promo applique au paiement RDV

  // Scroll automatique vers le haut a chaque changement d'etape
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step, bookingResult]);

  const EXCLUDED_VARIATIONS = ['Question Flash', 'Voyance/Guidance 15 Minutes', 'Guidance 15 Minutes'];

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
        if (location.state?.service) {
          const input = location.state.service;
          // Match par service_id (priorité), sinon par nom case-insensitive
          let svc = input.service_id
            ? res.data.find(s => s.service_id === input.service_id)
            : null;
          if (!svc && input.name) {
            const wanted = input.name.toLowerCase().trim();
            svc = res.data.find(s => (s.name || "").toLowerCase().trim() === wanted)
              || res.data.find(s => (s.name || "").toLowerCase().includes(wanted));
          }
          // Si rien trouvé, on n'affiche PAS l'objet partiel (qui aurait prix=0/durée vide).
          // On laisse l'utilisateur à l'étape 1 pour choisir.
          if (svc) {
            setSelectedService(svc);
            if (svc.location_modes?.length === 1) setLocationMode(svc.location_modes[0]);
            setStep(2);
          }
        }
      } catch (e) { toast.error("Erreur chargement services"); }
    };
    fetchServices();
    // location.state.service ne change pas pendant la session, juste valeur d'entree initiale
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedService) return;
    const fetchDays = async () => {
      try {
        const monthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
        const res = await axios.get(`${API}/appointments/available-days`, { params: { month: monthStr, service_id: selectedService.service_id } });
        setAvailableDays(res.data.available_days || []);
      } catch (e) { console.error(e); }
    };
    fetchDays();
  }, [selectedService, currentMonth]);

  useEffect(() => {
    if (!selectedDate || !selectedService) return;
    const fetchSlots = async () => {
      setSlotsLoading(true);
      try {
        // Format YYYY-MM-DD en heure locale — NE PAS utiliser toISOString() ici
        // (en Europe/Paris, minuit local = jour précédent en UTC → date décalée).
        const dateStr = formatLocalDate(selectedDate);
        const params = { date: dateStr, service_id: selectedService.service_id };
        if (selectedVariation?.variation_id) params.variation_id = selectedVariation.variation_id;
        const res = await axios.get(`${API}/appointments/available-slots`, { params });
        const slots = res.data.available_slots || [];
        setAvailableSlots(slots);
        // If a suggested slot was pre-selected, keep it; otherwise clear
        if (pendingSuggestedTime && slots.includes(pendingSuggestedTime)) {
          setSelectedSlot(pendingSuggestedTime);
          setPendingSuggestedTime(null);
        } else {
          setSelectedSlot(null);
        }
      } catch (e) { toast.error("Erreur chargement créneaux"); }
      finally { setSlotsLoading(false); }
    };
    fetchSlots();
    // pendingSuggestedTime est volontairement lu sans declencher de fetch (deja consomme via setPendingSuggestedTime(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedService, selectedVariation]);

  useEffect(() => {
    if (locationMode === 'distance') setPaymentMethod('square_online');
    else setPaymentMethod('pay_at_shop');
  }, [locationMode]);

  // Fetch suggested next slots when service/variation changes
  useEffect(() => {
    if (!selectedService || selectedService.is_async || selectedService.is_quote) {
      setSuggestedSlots([]);
      return;
    }
    const fetchSuggestions = async () => {
      setSuggestionsLoading(true);
      try {
        const params = { service_id: selectedService.service_id, limit: 6 };
        if (selectedVariation?.variation_id) params.variation_id = selectedVariation.variation_id;
        const res = await axios.get(`${API}/appointments/next-slots`, { params });
        setSuggestedSlots(res.data.suggestions || []);
      } catch (e) { console.error(e); }
      finally { setSuggestionsLoading(false); }
    };
    fetchSuggestions();
  }, [selectedService, selectedVariation]);

  const pickSuggestedSlot = (s) => {
    const [y, m, d] = s.date.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    setPendingSuggestedTime(s.time_slot);
    setSelectedDate(dt);
    setCurrentMonth(dt);
    setSelectedSlot(s.time_slot);
  };

  const selectService = (svc) => {
    setSelectedService(svc);
    setSelectedVariation(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    if (svc.location_modes?.length === 1) setLocationMode(svc.location_modes[0]);
    else setLocationMode(null);
    setStep(2);
  };

  const getCurrentPrice = () => selectedVariation ? selectedVariation.price : (selectedService?.price || 0);

  const [pendingAppointmentId, setPendingAppointmentId] = useState(null);
  const [resumePending, setResumePending] = useState(false);

  const _saveBookingDraft = () => {
    try {
      localStorage.setItem('b2v_booking_draft', JSON.stringify({
        savedAt: Date.now(),
        service_id: selectedService?.service_id,
        variation_id: selectedVariation?.variation_id || null,
        date: selectedDate ? selectedDate.toISOString() : null,
        slot: selectedSlot || null,
        locationMode: locationMode || null,
        paymentMethod,
        notes: notes || null,
      }));
    } catch (_e) { /* mode privé : skip */ }
  };

  const handleBookingClick = () => {
    const isAsync = !!selectedService?.is_async;
    if (!isAsync && (!selectedDate || !selectedSlot)) {
      toast.error("Choisis une date et un créneau");
      return;
    }
    if (isAsync && !notes?.trim()) {
      toast.error("Merci de renseigner tes informations");
      return;
    }
    if (!rgpdConsent) { toast.error("Merci d'accepter notre politique de confidentialité"); return; }
    // Pas connecté : on mémorise la réservation en cours puis on ouvre connexion/inscription.
    // Au retour (post-login), le tunnel reprend automatiquement vers le paiement.
    if (!user) {
      _saveBookingDraft();
      toast.info("Connecte-toi ou crée un compte pour finaliser");
      login('/booking');
      return;
    }
    // Connecté : on ouvre le recap a valider
    setShowPolicyModal(true);
  };

  const handleBooking = async () => {
    if (!user) { toast.info("Connecte-toi pour réserver"); login('/booking'); return; }
    if (!rgpdConsent) { toast.error("Merci d'accepter notre politique de confidentialité"); return; }
    const isAsync = !!selectedService?.is_async;
    if (!isAsync && (!selectedDate || !selectedSlot)) {
      toast.error("Choisis une date et un créneau");
      return;
    }
    setLoading(true);
    try {
      // Sauvegarde silencieuse du téléphone dans le profil s'il a changé
      if (phoneInput && phoneInput !== user?.phone) {
        try { await axios.put(`${API}/auth/profile`, { phone: phoneInput.trim() }, { withCredentials: true }); }
        catch (phoneErr) { console.warn("Phone silent-save failed (non-blocking):", phoneErr); }
      }
      const res = await axios.post(`${API}/appointments`, {
        service_id: selectedService.service_id,
        date: isAsync ? formatLocalDate(new Date()) : formatLocalDate(selectedDate),
        time_slot: isAsync ? "00:00" : selectedSlot,
        location_mode: locationMode || selectedService.location_modes?.[0] || 'presentiel',
        variation_id: selectedVariation?.variation_id || null,
        payment_method: isAsync ? "square_online" : paymentMethod,
        notes: notes || null
      }, { withCredentials: true });
      if (res.data.success) {
        setShowPolicyModal(false);
        if (res.data.payment_required) {
          setPendingAppointmentId(res.data.appointment_id);
          setStep(3);
        } else {
          setBookingResult(res.data);
          setStep(4);
          toast.success("Rendez-vous confirmé !");
        }
      }
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur lors de la réservation"); }
    finally { setLoading(false); }
  };

  const handleSquarePayment = async (sourceId, verificationToken) => {
    try {
      const res = await axios.post(`${API}/appointments/${pendingAppointmentId}/pay`, {
        source_id: sourceId,
        verification_token: verificationToken,
        promo_code: bookingPromo?.code || null,
      }, { withCredentials: true });
      if (res.data.success) {
        setBookingResult(res.data);
        setStep(4);
        toast.success("Paiement accepté ! Rendez-vous confirmé !");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de paiement. Réessaie.");
      throw error;
    }
  };

  // --- Reprise automatique du tunnel après connexion/inscription ---
  // Si un brouillon de réservation existe et que l'utilisateur est maintenant connecté,
  // on restaure la prestation + le créneau puis on enchaîne directement vers le paiement.
  useEffect(() => {
    if (!user || services.length === 0) return;
    let draftRaw = null;
    try { draftRaw = localStorage.getItem('b2v_booking_draft'); } catch (_e) { /* */ }
    if (!draftRaw) return;
    try { localStorage.removeItem('b2v_booking_draft'); } catch (_e) { /* */ }
    try {
      const d = JSON.parse(draftRaw);
      // TTL 30 min : un brouillon trop vieux ne doit pas déclencher une réservation surprise.
      if (!d.savedAt || (Date.now() - d.savedAt) > 30 * 60 * 1000) return;
      const svc = services.find(s => s.service_id === d.service_id);
      if (!svc) return;
      setSelectedService(svc);
      setSelectedVariation(d.variation_id ? (svc.variations || []).find(v => v.variation_id === d.variation_id) || null : null);
      if (d.locationMode) setLocationMode(d.locationMode);
      if (d.date) setSelectedDate(new Date(d.date));
      if (d.slot) { setPendingSuggestedTime(d.slot); setSelectedSlot(d.slot); }
      if (d.paymentMethod) setPaymentMethod(d.paymentMethod);
      if (d.notes) setNotes(d.notes);
      setRgpdConsent(true);
      setStep(2);
      setResumePending(true);
    } catch (_e) { /* brouillon invalide : on ignore */ }
    // On veut déclencher la reprise dès que user + services sont prêts
  }, [user, services]);

  useEffect(() => {
    if (!resumePending || !user || !selectedService) return;
    const isAsync = !!selectedService.is_async;
    if (!isAsync && (!selectedDate || !selectedSlot)) return; // on attend que le state soit restauré
    setResumePending(false);
    handleBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumePending, user, selectedService, selectedDate, selectedSlot]);

  const isDateAvailable = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return availableDays.includes(dateStr);
  };

  const morningSlots = availableSlots.filter(s => parseInt(s.split(':')[0]) < 12);
  const afternoonSlots = availableSlots.filter(s => parseInt(s.split(':')[0]) >= 14);

  return (
    <main className="pt-24 min-h-screen">
      <section className="py-16 bg-[#1B132B]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[#00E5FF] text-sm uppercase tracking-[0.2em] mb-4 block">Prends rendez-vous</span>
            <h1 className="font-serif text-4xl sm:text-5xl mb-4">Réservation</h1>
            <p className="text-[#B59ED1] max-w-xl mx-auto text-sm">Choisis ta prestation, sélectionne un créneau disponible et réserve en quelques clics.</p>
          </div>

          {/* Progress Steps - sticky */}
          <div className="sticky top-20 z-30 -mx-6 px-6 py-3 mb-8 bg-[#1B132B]/85 backdrop-blur-md border-b border-white/5" data-testid="booking-steps-sticky">
            <div className="flex items-center justify-center gap-2" data-testid="booking-steps">
              {[{num:1,label:'Prestation'},{num:2,label:'Date & Créneau'},{num:3,label:'Paiement'},{num:4,label:'Terminé'}].map((s, i) => (
                <React.Fragment key={s.num}>
                  {i > 0 && <div className={`w-8 h-px ${step >= s.num ? 'bg-[#D926A9]' : 'bg-white/10'}`} />}
                  <button onClick={() => { if (s.num < step && s.num < 4) setStep(s.num); }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${step === s.num ? 'bg-[#D926A9] text-white' : step > s.num ? 'bg-[#D926A9]/20 text-[#D926A9] cursor-pointer' : 'bg-white/5 text-[#B59ED1]'}`}
                    data-testid={`step-${s.num}`}>
                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] bg-white/10">{step > s.num ? '✓' : s.num}</span>
                    <span className="hidden sm:inline">{s.label}</span>
                  </button>
                </React.Fragment>
              ))}
            </div>
            {/* Barre de progression fine */}
            <div className="mt-2 h-0.5 bg-white/5 rounded-full overflow-hidden max-w-md mx-auto">
              <motion.div
                className="h-full bg-gradient-to-r from-[#D926A9] to-[#00E5FF]"
                initial={{ width: 0 }}
                animate={{ width: ((step - 1) / 3) * 100 + '%' }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>
          </div>

          {/* STEP 1 */}
          {step === 1 && (
            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="max-w-3xl mx-auto">
              <h2 className="font-serif text-2xl mb-6 text-center">Choisis ta prestation</h2>
              <div className="space-y-3">
                {(() => {
                  // Custom display order requested by Guillaume
                  const ORDER = ['Voyance', 'Pranic Healing', 'Bilan de Vie', 'Purification', 'Consultation par mail', 'Numérologie'];
                  const orderIndex = (s) => {
                    const name = (s.name || '').toLowerCase();
                    const idx = ORDER.findIndex(k => name.includes(k.toLowerCase()));
                    return idx === -1 ? 999 : idx;
                  };
                  return [...services].sort((a, b) => orderIndex(a) - orderIndex(b));
                })().map(svc => {
                  const filteredVars = svc.variations?.filter(v => !EXCLUDED_VARIATIONS.includes(v.name)) || [];
                  const minPrice = svc.has_variations && filteredVars.length > 1 ? Math.min(...filteredVars.map(v => v.price)) : svc.price;
                  return (
                    <div key={svc.service_id} onClick={() => selectService(svc)}
                      className={`p-4 rounded-xl border cursor-pointer transition-all flex items-center gap-4 ${selectedService?.service_id === svc.service_id ? 'border-[#D926A9] bg-[#D926A9]/10' : 'border-white/10 bg-[#261B3B] hover:border-white/20'}`}
                      data-testid={`booking-svc-${svc.service_id}`}>
                      {svc.image_url && <img src={svc.image_url.startsWith('/') ? `${BACKEND_URL}${svc.image_url}` : svc.image_url} alt={svc.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" onError={(e) => {e.target.style.display='none';}} />}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium">{svc.name}</h3>
                        <p className="text-[#B59ED1] text-sm truncate">{svc.short_description}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[#00E5FF] text-sm font-semibold">{svc.has_variations && filteredVars.length > 1 ? `À partir de ${minPrice} €` : `${svc.price} €`}</span>
                          <span className="text-xs text-[#B59ED1]">{svc.duration_minutes} min</span>
                          {svc.location_modes?.map(m => <Badge key={m} className={`text-[10px] ${m==='presentiel'?'bg-[#D926A9]/10 text-[#D926A9]':'bg-[#00E5FF]/10 text-[#00E5FF]'}`}>{m==='presentiel'?presentielLabel(svc):'Distance'}</Badge>)}
                        </div>
                      </div>
                      <ArrowRight className={`w-5 h-5 flex-shrink-0 ${selectedService?.service_id === svc.service_id ? 'text-[#D926A9]' : 'text-white/20'}`} />
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* STEP 2 */}
          {step === 2 && selectedService && (
            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}>
              <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-[#261B3B] border border-white/10 max-w-3xl mx-auto cursor-pointer hover:border-white/20" onClick={() => setStep(1)} data-testid="selected-service-bar">
                {selectedService.image_url && <img src={selectedService.image_url.startsWith('/') ? `${BACKEND_URL}${selectedService.image_url}` : selectedService.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" onError={(e) => {e.target.style.display='none';}} />}
                <div className="flex-1">
                  <span className="font-medium text-sm">{selectedVariation?.name || selectedService.name}</span>
                  {!selectedService.is_async && (
                    <span className="text-[#B59ED1] text-xs ml-2">{(selectedVariation?.duration_minutes || selectedService.duration_minutes)} min</span>
                  )}
                </div>
                <span className="text-[#00E5FF] font-semibold text-sm">{getCurrentPrice()} €</span>
                <span className="text-[#B59ED1] text-xs">Modifier</span>
              </div>

              {/* Service description (enrichie pour les services prioritaires) */}
              <div className="max-w-3xl mx-auto mb-6 p-5 rounded-xl bg-[#261B3B] border border-white/10">
                <h3 className="font-serif text-lg mb-2">{selectedVariation?.name || selectedService.name}</h3>
                {(() => {
                  const enriched = enrichService(selectedService);
                  return (
                    <>
                      <p className="text-[#B59ED1] text-sm leading-relaxed whitespace-pre-line mb-2">{enriched.description}</p>
                      {enriched.learnMore && (
                        <Link
                          to={enriched.learnMore.url}
                          className="inline-flex items-center gap-1 text-[#00E5FF] hover:text-[#D926A9] transition-colors text-xs mb-3 underline-offset-4 hover:underline"
                          data-testid="booking-learn-more"
                        >
                          {enriched.learnMore.label}
                        </Link>
                      )}
                    </>
                  );
                })()}
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  {!selectedService.is_async && (
                    <span className="text-[#00E5FF]">{(selectedVariation?.duration_minutes || selectedService.duration_minutes)} min</span>
                  )}
                  <span className="text-[#D926A9] font-semibold">{getCurrentPrice()} €</span>
                  {selectedService.is_async ? (
                    <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] text-xs">
                      <Mail className="w-3 h-3 mr-1" />Reçu par email sous {selectedService.async_delivery_days || 3} jours
                    </Badge>
                  ) : (
                    selectedService.location_modes?.map(m => (
                      <Badge key={m} className={`text-xs ${m === 'presentiel' ? 'bg-[#D926A9]/10 text-[#D926A9]' : 'bg-[#00E5FF]/10 text-[#00E5FF]'}`}>
                        {m === 'presentiel' ? presentielLabel(selectedService, { long: true }) : 'À distance'}
                      </Badge>
                    ))
                  )}
                </div>
              </div>

              {selectedService.is_async ? (
                /* Service livre par email - pas de creneau, juste un recap + CTA */
                <div className="max-w-2xl mx-auto space-y-5">
                  {selectedService.has_variations && selectedService.variations?.filter(v => !EXCLUDED_VARIATIONS.includes(v.name)).length > 1 && (
                    <Card className="bg-[#261B3B] border-white/10"><CardContent className="pt-5">
                      <label className="text-sm text-[#B59ED1] mb-2 block">Formule</label>
                      <Select value={selectedVariation?.variation_id || ""} onValueChange={(val) => setSelectedVariation(selectedService.variations.find(v => v.variation_id === val))}>
                        <SelectTrigger className="bg-[#1B132B] border-white/10" data-testid="variation-select"><SelectValue placeholder="Choisir une formule" /></SelectTrigger>
                        <SelectContent className="bg-[#261B3B] border-white/10">
                          {selectedService.variations.filter(v => !EXCLUDED_VARIATIONS.includes(v.name)).map(v => (
                            <SelectItem key={v.variation_id} value={v.variation_id} className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">
                              {v.name} — {v.price} €
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent></Card>
                  )}

                  <Card className="bg-[#00E5FF]/5 border-[#00E5FF]/20">
                    <CardContent className="pt-5">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#00E5FF]/15 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-[#00E5FF]" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-white mb-1">Service livré par email</p>
                          <p className="text-sm text-[#B59ED1] leading-relaxed">
                            Tu recevras ta consultation détaillée par email à <span className="text-[#00E5FF]">{user?.email || 'ton adresse'}</span> sous <strong className="text-white">{selectedService.async_delivery_days || 3} jours</strong> après le paiement.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-[#261B3B] border-white/10">
                    <CardContent className="pt-5">
                      <label className="text-sm text-[#B59ED1] mb-2 block">
                        Tes informations / questions {selectedService.name?.toLowerCase().includes('numerologie') || selectedService.name?.toLowerCase().includes('numérologie') ? '(date de naissance, prénom complet)' : '(détaille ta demande)'}
                      </label>
                      <Textarea
                        placeholder={
                          selectedService.name?.toLowerCase().includes('numerologie') || selectedService.name?.toLowerCase().includes('numérologie')
                            ? "Date de naissance complete (jour/mois/annee), prenoms et nom de naissance, et toute precision utile..."
                            : "Decris ce sur quoi tu souhaites etre eclaire(e) — questions, situation, contexte..."
                        }
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="bg-[#1B132B] border-white/10 text-sm min-h-[140px]"
                        data-testid="async-notes"
                        required
                      />
                      <p className="text-[10px] text-[#B59ED1] mt-2">Ces informations sont essentielles pour la qualité de ta consultation.</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-[#261B3B] border-[#D926A9]/30">
                    <CardContent className="pt-5">
                      <div className="space-y-2 mb-4 text-sm">
                        <div className="flex justify-between"><span className="text-[#B59ED1]">Prestation</span><span>{selectedVariation?.name || selectedService.name}</span></div>
                        <div className="flex justify-between"><span className="text-[#B59ED1]">Livraison</span><span className="text-[#00E5FF]">Email sous {selectedService.async_delivery_days || 3} jours</span></div>
                        <div className="flex justify-between"><span className="text-[#B59ED1]">Paiement</span><span className="text-[#00E5FF]">En ligne (sécurisé)</span></div>
                        <Separator className="bg-white/10 my-2" />
                        <div className="flex justify-between text-lg"><span className="font-medium">Total</span><span className="font-serif text-[#D926A9]">{getCurrentPrice()} €</span></div>
                      </div>
                      <label className="flex items-start gap-2 text-xs text-[#B59ED1] mb-3 cursor-pointer leading-relaxed">
                        <input
                          type="checkbox"
                          checked={rgpdConsent}
                          onChange={(e) => setRgpdConsent(e.target.checked)}
                          className="mt-0.5 w-4 h-4 rounded accent-[#D926A9] flex-shrink-0"
                          data-testid="booking-async-rgpd-checkbox"
                        />
                        <span>
                          J'accepte la <Link to="/politique-de-confidentialite" target="_blank" className="text-[#00E5FF] hover:underline">politique de confidentialité</Link> et les <Link to="/cgv" target="_blank" className="text-[#00E5FF] hover:underline">CGV</Link>.
                        </span>
                      </label>
                      <Button
                        onClick={handleBookingClick}
                        disabled={loading || !notes?.trim() || !rgpdConsent}
                        className="w-full gradient-primary"
                        data-testid="confirm-booking-async"
                      >
                        {loading ? "Traitement..." : `Continuer vers le paiement (${getCurrentPrice()} €)`}
                      </Button>
                      {!notes?.trim() && <p className="text-[10px] text-[#D926A9] mt-2 text-center">Merci de renseigner tes informations avant de continuer.</p>}
                    </CardContent>
                  </Card>
                </div>
              ) : (
              <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-5">
                  {selectedService.has_variations && selectedService.variations?.filter(v => !EXCLUDED_VARIATIONS.includes(v.name)).length > 1 && (
                    <Card className="bg-[#261B3B] border-white/10"><CardContent className="pt-5">
                      <label className="text-sm text-[#B59ED1] mb-2 block">Formule</label>
                      <Select value={selectedVariation?.variation_id || ""} onValueChange={(val) => setSelectedVariation(selectedService.variations.find(v => v.variation_id === val))}>
                        <SelectTrigger className="bg-[#1B132B] border-white/10" data-testid="variation-select"><SelectValue placeholder="Choisir une formule" /></SelectTrigger>
                        <SelectContent className="bg-[#261B3B] border-white/10">
                          {selectedService.variations.filter(v => !EXCLUDED_VARIATIONS.includes(v.name)).map(v => (
                            <SelectItem key={v.variation_id} value={v.variation_id} className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">
                              {v.name} — {v.price} €{v.duration_minutes ? ` · ${v.duration_minutes} min` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent></Card>
                  )}
                  {selectedService.location_modes && (
                    <Card className="bg-[#261B3B] border-white/10"><CardContent className="pt-5">
                      <label className="text-sm text-[#B59ED1] mb-3 block font-medium">Mode de consultation</label>
                      {selectedService.location_modes.length === 2 ? (
                        <div className="grid grid-cols-2 gap-3">
                          <button type="button" onClick={() => setLocationMode('presentiel')} className={`p-5 rounded-xl border-2 text-center transition-all ${locationMode==='presentiel'?'border-[#D926A9] bg-[#D926A9]/15 text-[#D926A9] shadow-[0_0_25px_rgba(217,38,169,0.15)]':'border-white/10 bg-[#1B132B] text-[#B59ED1] hover:border-white/30'}`} data-testid="location-presentiel">
                            <MapPin className="w-7 h-7 mx-auto mb-2" />
                            <p className="text-base font-semibold mb-0.5">En boutique</p>
                            <p className="text-[11px] opacity-75">Sur place à Montélimar</p>
                          </button>
                          <button type="button" onClick={() => setLocationMode('distance')} className={`p-5 rounded-xl border-2 text-center transition-all ${locationMode==='distance'?'border-[#00E5FF] bg-[#00E5FF]/15 text-[#00E5FF] shadow-[0_0_25px_rgba(0,229,255,0.15)]':'border-white/10 bg-[#1B132B] text-[#B59ED1] hover:border-white/30'}`} data-testid="location-distance">
                            <Phone className="w-7 h-7 mx-auto mb-2" />
                            <p className="text-base font-semibold mb-0.5">À distance</p>
                            <p className="text-[11px] opacity-75">Par téléphone ou visio</p>
                          </button>
                        </div>
                      ) : (
                        <div className={`p-5 rounded-xl border-2 text-center font-medium ${selectedService.location_modes[0]==='presentiel'?'border-[#D926A9]/30 bg-[#D926A9]/10 text-[#D926A9]':'border-[#00E5FF]/30 bg-[#00E5FF]/10 text-[#00E5FF]'}`}>
                          {selectedService.location_modes[0]==='presentiel'?<><MapPin className="w-6 h-6 mx-auto mb-1" /><p className="text-base">{presentielLabel(selectedService, { long: true })}</p></>:<><Phone className="w-6 h-6 mx-auto mb-1" /><p className="text-base">À distance</p></>}
                        </div>
                      )}
                    </CardContent></Card>
                  )}
                  <Card className="bg-[#261B3B] border-white/10"><CardContent className="pt-5 flex justify-center">
                    <CalendarComponent mode="single" selected={selectedDate} onSelect={(d) => {setSelectedDate(d); setSelectedSlot(null); setPendingSuggestedTime(null);}}
                      month={currentMonth} onMonthChange={setCurrentMonth}
                      disabled={(date) => { const today = new Date(); today.setHours(0,0,0,0); return date < today || !isDateAvailable(date); }}
                      className="rounded-md" data-testid="booking-calendar" />
                  </CardContent></Card>
                </div>

                <div className="space-y-5">
                  <Card className="bg-[#261B3B] border-white/10"><CardHeader className="pb-3"><CardTitle className="text-base font-medium">Créneaux disponibles</CardTitle></CardHeader><CardContent>
                    {!selectedDate ? <p className="text-[#B59ED1] text-center py-4 text-sm">Sélectionne une date</p>
                    : slotsLoading ? <div className="flex justify-center py-6"><div className="w-6 h-6 border-2 border-[#D926A9] border-t-transparent rounded-full animate-spin" /></div>
                    : availableSlots.length === 0 ? <p className="text-[#B59ED1] text-center py-4 text-sm">Aucun créneau disponible</p>
                    : <div className="space-y-4">
                        {morningSlots.length > 0 && <div><p className="text-xs text-[#B59ED1] mb-2 uppercase tracking-wider">Matin</p><div className="grid grid-cols-3 gap-2">{morningSlots.map(slot => (
                          <button key={slot} onClick={() => setSelectedSlot(slot)} className={`py-2.5 rounded-lg text-sm font-medium transition-all ${selectedSlot===slot?'bg-[#D926A9] text-white':'bg-[#1B132B] border border-white/10 text-[#E2D5F8] hover:border-[#D926A9]/40'}`} data-testid={`slot-${slot}`}>{slot}</button>
                        ))}</div></div>}
                        {afternoonSlots.length > 0 && <div><p className="text-xs text-[#B59ED1] mb-2 uppercase tracking-wider">Après-midi</p><div className="grid grid-cols-3 gap-2">{afternoonSlots.map(slot => (
                          <button key={slot} onClick={() => setSelectedSlot(slot)} className={`py-2.5 rounded-lg text-sm font-medium transition-all ${selectedSlot===slot?'bg-[#D926A9] text-white':'bg-[#1B132B] border border-white/10 text-[#E2D5F8] hover:border-[#D926A9]/40'}`} data-testid={`slot-${slot}`}>{slot}</button>
                        ))}</div></div>}
                      </div>}
                  </CardContent></Card>

                  {selectedSlot && locationMode === 'presentiel' && (
                    <Card className="bg-[#261B3B] border-white/10"><CardContent className="pt-5">
                      <label className="text-sm text-[#B59ED1] mb-3 block font-medium">Mode de paiement</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button type="button" onClick={() => setPaymentMethod('pay_at_shop')} className={`p-5 rounded-xl border-2 text-center transition-all ${paymentMethod==='pay_at_shop'?'border-[#D926A9] bg-[#D926A9]/15 text-[#D926A9] shadow-[0_0_25px_rgba(217,38,169,0.15)]':'border-white/10 bg-[#1B132B] text-[#B59ED1] hover:border-white/30'}`} data-testid="pay-at-shop">
                          <MapPin className="w-7 h-7 mx-auto mb-2" />
                          <p className="text-base font-semibold mb-0.5">En boutique</p>
                          <p className="text-[11px] opacity-75">Espèces ou CB</p>
                        </button>
                        <button type="button" onClick={() => setPaymentMethod('square_online')} className={`p-5 rounded-xl border-2 text-center transition-all ${paymentMethod==='square_online'?'border-[#00E5FF] bg-[#00E5FF]/15 text-[#00E5FF] shadow-[0_0_25px_rgba(0,229,255,0.15)]':'border-white/10 bg-[#1B132B] text-[#B59ED1] hover:border-white/30'}`} data-testid="pay-online">
                          <CreditCard className="w-7 h-7 mx-auto mb-2" />
                          <p className="text-base font-semibold mb-0.5">En ligne</p>
                          <p className="text-[11px] opacity-75">CB sécurisée Square</p>
                        </button>
                      </div>
                    </CardContent></Card>
                  )}

                  {selectedSlot && (
                    <Card className="bg-[#261B3B] border-[#D926A9]/30"><CardContent className="pt-5">
                      <div className="space-y-2 mb-4 text-sm">
                        <div className="flex justify-between"><span className="text-[#B59ED1]">Prestation</span><span>{selectedVariation?.name || selectedService.name}</span></div>
                        <div className="flex justify-between"><span className="text-[#B59ED1]">Date</span><span>{selectedDate.toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long'})}</span></div>
                        <div className="flex justify-between"><span className="text-[#B59ED1]">Heure</span><span>{selectedSlot}</span></div>
                        <div className="flex justify-between"><span className="text-[#B59ED1]">Mode</span><span className={locationMode==='distance'?'text-[#00E5FF]':'text-[#D926A9]'}>{locationMode==='distance'?'À distance':'En boutique'}</span></div>
                        <div className="flex justify-between"><span className="text-[#B59ED1]">Paiement</span><span>{paymentMethod==='pay_at_shop'?'En boutique':'En ligne'}</span></div>
                        <Separator className="bg-white/10 my-2" />
                        <div className="flex justify-between text-lg"><span className="font-medium">Total</span><span className="font-serif text-[#D926A9]">{getCurrentPrice()} €</span></div>
                      </div>
                      <Textarea placeholder="Notes (optionnel)" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-[#1B132B] border-white/10 mb-3 text-sm min-h-[60px]" data-testid="booking-notes" />
                      <label className="flex items-start gap-2 text-xs text-[#B59ED1] mb-3 cursor-pointer leading-relaxed" data-testid="booking-rgpd-wrapper">
                        <input
                          type="checkbox"
                          checked={rgpdConsent}
                          onChange={(e) => setRgpdConsent(e.target.checked)}
                          className="mt-0.5 w-4 h-4 rounded accent-[#D926A9] flex-shrink-0"
                          data-testid="booking-rgpd-checkbox"
                        />
                        <span>
                          J'accepte la <Link to="/politique-de-confidentialite" target="_blank" className="text-[#00E5FF] hover:underline">politique de confidentialité</Link> et les <Link to="/cgv" target="_blank" className="text-[#00E5FF] hover:underline">CGV</Link>.
                        </span>
                      </label>
                      <Button onClick={handleBookingClick} disabled={loading || !rgpdConsent} className="w-full gradient-primary" data-testid="confirm-booking">
                        {loading ? "Réservation..." : !user ? "Continuer (connexion / création de compte)" : paymentMethod === 'square_online' ? `Continuer vers le paiement (${getCurrentPrice()} €)` : "Confirmer la réservation"}
                      </Button>
                      <p className="text-[10px] text-[#B59ED1] mt-3 text-center leading-relaxed">Annulation ou modification gratuite jusqu'à 3h avant le rendez-vous.</p>
                    </CardContent></Card>
                  )}
                </div>
              </div>
              </div>
              )}
            </motion.div>
          )}
          {/* STEP 3: Payment */}
          {step === 3 && pendingAppointmentId && selectedService && (
            <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="max-w-lg mx-auto">
              <h2 className="font-serif text-2xl mb-6 text-center">Paiement sécurisé</h2>
              <Card className="bg-[#261B3B] border-white/10 mb-4">
                <CardContent className="pt-5 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#B59ED1]">Prestation</span><span>{selectedVariation?.name || selectedService.name}</span></div>
                  {selectedService.is_async ? (
                    <div className="flex justify-between"><span className="text-[#B59ED1]">Livraison</span><span className="text-[#00E5FF]">Email sous {selectedService.async_delivery_days || 3} jours</span></div>
                  ) : (
                    <>
                      <div className="flex justify-between"><span className="text-[#B59ED1]">Date</span><span>{selectedDate?.toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long'})}</span></div>
                      <div className="flex justify-between"><span className="text-[#B59ED1]">Heure</span><span>{selectedSlot}</span></div>
                    </>
                  )}
                  <Separator className="bg-white/10 my-2" />
                  <div className="flex justify-between"><span className="text-[#B59ED1]">Sous-total</span><span>{Number(getCurrentPrice()).toFixed(2)} €</span></div>
                  {bookingPromo && (
                    <div className="flex justify-between text-emerald-300" data-testid="booking-promo-line">
                      <span>Code {bookingPromo.code}</span>
                      <span>−{Number(bookingPromo.discount_amount).toFixed(2)} €</span>
                    </div>
                  )}
                  <Separator className="bg-white/10 my-2" />
                  <div className="flex justify-between text-lg"><span className="font-medium">Total à payer</span><span className="font-serif text-[#D926A9]">{Math.max(0, Number(getCurrentPrice()) - Number(bookingPromo?.discount_amount || 0)).toFixed(2)} €</span></div>
                  <Separator className="bg-white/10 my-2" />
                  <div>
                    <label className="text-xs text-[#B59ED1] block mb-1">Téléphone (pour te joindre le jour du RDV)</label>
                    <input
                      type="tel" inputMode="tel" autoComplete="tel"
                      value={phoneInput}
                      onChange={(e) => setPhoneInput(e.target.value)}
                      onBlur={() => { if (phoneInput && phoneInput.trim() !== (user?.phone || '')) { axios.put(`${API}/auth/profile`, { phone: phoneInput.trim() }, { withCredentials: true }).catch(() => {}); } }}
                      placeholder="06 12 34 56 78"
                      className="w-full h-9 px-3 rounded-md bg-[#1B132B] border border-white/10 text-sm text-white placeholder:text-[#B59ED1]/60 focus:outline-none focus:border-[#D926A9]"
                      data-testid="booking-phone-payment"
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-[#261B3B] border-white/10 mb-4">
                <CardContent className="pt-5">
                  <p className="text-xs text-[#B59ED1] mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#00E5FF]" /> Tu as un code promo ?</p>
                  <PromoCodeInput
                    subtotal={Number(getCurrentPrice())}
                    surface="booking"
                    applied={bookingPromo}
                    onApplied={(p) => { setBookingPromo(p); toast.success(`Code ${p.code} appliqué : −${p.discount_amount.toFixed(2)} €`); }}
                    onCleared={() => setBookingPromo(null)}
                    testIdPrefix="booking-promo"
                  />
                </CardContent>
              </Card>
              <Card className="bg-[#261B3B] border-white/10">
                <CardContent className="pt-5">
                  <SquarePaymentForm
                    amount={Math.max(0, Number(getCurrentPrice()) - Number(bookingPromo?.discount_amount || 0))}
                    onSuccess={handleSquarePayment}
                    buttonText="Payer"
                  />
                  <p className="text-[10px] text-[#B59ED1] mt-3 text-center">Paiement sécurisé par Square. Tes données bancaires ne sont jamais stockées sur nos serveurs.</p>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* STEP 4 */}
          {step === 4 && bookingResult && (
            <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} className="max-w-lg mx-auto text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6"><Check className="w-8 h-8 text-green-400" /></div>
              <h2 className="font-serif text-3xl mb-3">Rendez-vous confirmé !</h2>
              <p className="text-[#B59ED1] mb-8">{paymentMethod==='pay_at_shop'?'Ton rendez-vous est confirmé. Le paiement se fera en boutique.':'Ton paiement a été accepté et ton rendez-vous est confirmé.'}</p>
              <Card className="bg-[#261B3B] border-white/10 text-left mb-6"><CardContent className="pt-5 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-[#B59ED1]">Service</span><span>{selectedVariation?.name || selectedService?.name}</span></div>
                <div className="flex justify-between"><span className="text-[#B59ED1]">Date</span><span>{selectedDate?.toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long'})}</span></div>
                <div className="flex justify-between"><span className="text-[#B59ED1]">Heure</span><span>{selectedSlot}</span></div>
                <div className="flex justify-between"><span className="text-[#B59ED1]">Mode</span><span>{locationMode==='distance'?'À distance':'En boutique'}</span></div>
                <Separator className="bg-white/10" />
                <div className="flex justify-between text-lg"><span>Total</span><span className="font-serif text-[#D926A9]">{getCurrentPrice()} €</span></div>
              </CardContent></Card>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate('/dashboard')} className="gradient-primary" data-testid="go-dashboard">Mes rendez-vous</Button>
                <Button onClick={() => {setStep(1);setSelectedService(null);setSelectedDate(null);setSelectedSlot(null);setBookingResult(null);}} variant="outline" className="border-white/10">Nouveau rendez-vous</Button>
              </div>
            </motion.div>
          )}

          {/* Aide : appeler en cas de difficulté pour réserver en ligne */}
          <p className="mt-8 text-center text-xs text-[#B59ED1] max-w-md mx-auto leading-relaxed" data-testid="booking-help-call">
            Une difficulté pour prendre rendez-vous en ligne ?{' '}
            <a href="tel:+33768489056" className="text-[#00E5FF] hover:underline font-medium whitespace-nowrap">
              Appelle-moi au 07&nbsp;68&nbsp;48&nbsp;90&nbsp;56
            </a>{' '}et on réserve ton créneau ensemble.
          </p>
        </div>
      </section>
      <ConsultationPolicyModal
        open={showPolicyModal}
        onOpenChange={setShowPolicyModal}
        onAccept={handleBooking}
        loading={loading}
      />
    </main>
  );
}
